import { cloneDeep } from 'lodash';
import { Profile } from '../../Types';
import {
  CommentThread,
  CommentThreadType,
} from '../../api/CoachingComments/schema';

export const findThread = (
  threads: CommentThread[],
  type: CommentThreadType
): CommentThread | undefined =>
  threads.find(thread => thread.commentThreadType === type);

export const createThread = (
  threadType: CommentThreadType,
  participants: string[]
): CommentThread => ({
  commentThreadType: threadType,
  commentThreadParticipants: participants,
  comments: [],
});

export const findTmOmThreadByTm = (
  threads: CommentThread[],
  tmAlias: string
): CommentThread | undefined =>
  threads.find(
    thread =>
      thread.commentThreadType === CommentThreadType.TM_OM1 &&
      thread.commentThreadParticipants.includes(tmAlias)
  );

export const ensureParticipantsIncluded = (
  current: string[],
  required: string[]
): string[] => {
  const uniqueParticipants = new Set(current);

  required.forEach(participant => {
    uniqueParticipants.add(participant);
  });

  return Array.from(uniqueParticipants);
};

export const handleCSAThreads = (
  threads: CommentThread[],
  requesterUsername: string,
  csaManagerAlias: string
): CommentThread[] => {
  const updatedThreads = cloneDeep(threads);

  const csaTmThread = findThread(updatedThreads, CommentThreadType.CSA_TM);

  const requiredParticipantsSet = new Set([requesterUsername, csaManagerAlias]);
  const requiredParticipants = Array.from(requiredParticipantsSet);

  if (!csaTmThread) {
    updatedThreads.push(
      createThread(CommentThreadType.CSA_TM, requiredParticipants)
    );
  } else {
    csaTmThread.commentThreadParticipants = ensureParticipantsIncluded(
      csaTmThread.commentThreadParticipants,
      requiredParticipants
    );
  }

  return updatedThreads;
};

export const handleTMThreads = (
  threads: CommentThread[],
  requesterUsername: string,
  csaAlias: string,
  csaManagerAlias: string,
  managerAlias: string | undefined,
  managerProfile: Profile | null
): CommentThread[] => {
  const updatedThreads = cloneDeep(threads);

  const csaTmThread = findThread(updatedThreads, CommentThreadType.CSA_TM);
  const csaTmParticipantsSet = new Set([requesterUsername, csaAlias]);

  if (requesterUsername !== csaManagerAlias) {
    csaTmParticipantsSet.add(csaManagerAlias);
  }

  if (managerProfile?.proxyCoaches?.length) {
    managerProfile.proxyCoaches.forEach(proxyCoach => {
      csaTmParticipantsSet.add(proxyCoach.tmEntity.alias);
    });
  }

  const csaTmParticipants = Array.from(csaTmParticipantsSet);

  if (!csaTmThread) {
    updatedThreads.push(
      createThread(CommentThreadType.CSA_TM, csaTmParticipants)
    );
  } else {
    csaTmThread.commentThreadParticipants = ensureParticipantsIncluded(
      csaTmThread.commentThreadParticipants,
      csaTmParticipants
    );
  }

  if (managerAlias) {
    const tmOmThread = findTmOmThreadByTm(updatedThreads, requesterUsername);
    if (!tmOmThread) {
      updatedThreads.push(
        createThread(CommentThreadType.TM_OM1, [
          requesterUsername,
          managerAlias,
        ])
      );
    } else {
      tmOmThread.commentThreadParticipants = ensureParticipantsIncluded(
        tmOmThread.commentThreadParticipants,
        [managerAlias]
      );
    }
  }
  return updatedThreads;
};

export const handleOMThreads = (
  threads: CommentThread[],
  requesterUsername: string,
  managerAlias: string | undefined,
  managerProfile: Profile | null,
  csaAlias: string,
  csaManagerAlias: string
): CommentThread[] => {
  const updatedThreads = cloneDeep(threads);

  if (csaManagerAlias && csaManagerAlias !== requesterUsername) {
    const omCsaManagerThread = findTmOmThreadByTm(
      updatedThreads,
      csaManagerAlias
    );
    if (!omCsaManagerThread) {
      updatedThreads.push(
        createThread(CommentThreadType.TM_OM1, [
          requesterUsername,
          csaManagerAlias,
        ])
      );
    } else {
      omCsaManagerThread.commentThreadParticipants = ensureParticipantsIncluded(
        omCsaManagerThread.commentThreadParticipants,
        [requesterUsername]
      );
    }
  }
  if (managerProfile?.proxyCoaches?.length) {
    managerProfile.proxyCoaches.forEach(proxyCoach => {
      const proxyAlias = proxyCoach.tmEntity.alias;
      if (proxyAlias && proxyAlias !== requesterUsername) {
        const proxyThread = findTmOmThreadByTm(updatedThreads, proxyAlias);
        if (!proxyThread) {
          updatedThreads.push(
            createThread(CommentThreadType.TM_OM1, [
              requesterUsername,
              proxyAlias,
            ])
          );
        } else {
          proxyThread.commentThreadParticipants = ensureParticipantsIncluded(
            proxyThread.commentThreadParticipants,
            [requesterUsername]
          );
        }
      }
    });
  }

  const csaTmThread = findThread(updatedThreads, CommentThreadType.CSA_TM);
  const requiredParticipantsSet = new Set([csaAlias, csaManagerAlias]);

  if (managerProfile?.proxyCoaches?.length) {
    managerProfile.proxyCoaches.forEach(proxyCoach => {
      requiredParticipantsSet.add(proxyCoach.tmEntity.alias);
    });
  }

  const requiredParticipants = Array.from(requiredParticipantsSet);

  if (!csaTmThread) {
    updatedThreads.push(
      createThread(CommentThreadType.CSA_TM, requiredParticipants)
    );
  } else {
    csaTmThread.commentThreadParticipants = ensureParticipantsIncluded(
      csaTmThread.commentThreadParticipants,
      requiredParticipants
    );
  }

  return updatedThreads;
};

export const processCommentThreads = (
  existingThreads: CommentThread[],
  isOm: boolean,
  isTm: boolean,
  requesterUsername: string,
  profile: Profile,
  managerProfile: Profile | null
): CommentThread[] => {
  const csaManagerAlias = profile.managers[0]?.alias ?? '';
  const managerAlias = managerProfile?.managers[0]?.alias ?? '';
  const csaAlias = profile.user.alias;

  if (!isOm && !isTm) {
    return handleCSAThreads(
      existingThreads,
      requesterUsername,
      csaManagerAlias
    );
  }

  if (isTm && !isOm) {
    return handleTMThreads(
      existingThreads,
      requesterUsername,
      csaAlias,
      csaManagerAlias,
      managerAlias,
      managerProfile
    );
  }

  if (isOm) {
    return handleOMThreads(
      existingThreads,
      requesterUsername,
      managerAlias,
      managerProfile,
      csaAlias,
      csaManagerAlias
    );
  }
  return existingThreads;
};
